import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { PermissionGuard } from "../../components/PermissionGuard";
import { QuickBookService } from "./Service";
import { quickBookModeLabel, quickBookStatusLabel } from "./display";
import { catalogBreadcrumbText } from "./catalogModel";

const money = (paise) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
}).format(Number(paise || 0) / 100);

const EmptyRows = () => (
  <Box sx={{ display: "grid", height: "100%", placeItems: "center", p: 2 }}>
    <Typography color="text.secondary">No Quick Book services found.</Typography>
  </Box>
);

export default function QuickBookServices() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [filters, setFilters] = useState({ search: "", mode: "", status: "" });
  const { control, handleSubmit, reset } = useForm({ defaultValues: filters });

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-services" });
  }, [dispatch]);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await QuickBookService.list({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        ...filters,
      });
      if (response?.success === 1 || response?.success === "1") {
        setRows(response.data?.items || []);
        setRowCount(response.data?.pagination?.totalItems || 0);
      } else {
        setRows([]);
        setRowCount(0);
        setError(response?.message || "Quick Book services could not be loaded.");
      }
    } catch {
      setRows([]);
      setRowCount(0);
      setError("Quick Book services could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const columns = useMemo(() => [
    {
      field: "customerTitle",
      headerName: "Quick Book service",
      minWidth: 220,
      flex: 1.4,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: ".75rem", fontWeight: 600 }}>{row.customerTitle}</Typography>
          <Typography color="text.secondary" sx={{ fontSize: ".65rem" }}>
            {[row.canonicalService?.title || `Service #${row.serviceMasterId}`, row.serviceBrand?.title || row.brandTitle].filter(Boolean).join(" · ")}
          </Typography>
          {catalogBreadcrumbText(row.catalogBreadcrumb) && <Typography color="primary.main" sx={{ fontSize: ".62rem" }}>{catalogBreadcrumbText(row.catalogBreadcrumb)}</Typography>}
        </Box>
      ),
    },
    {
      field: "mode",
      headerName: "Mode",
      minWidth: 135,
      renderCell: ({ value }) => (
        <Chip size="small" label={quickBookModeLabel(value)} />
      ),
    },
    {
      field: "unitAmountPaise",
      headerName: "Unit price",
      minWidth: 130,
      renderCell: ({ row }) => `${money(row.unitAmountPaise)} / ${row.unitLabel}`,
    },
    {
      field: "duration",
      headerName: "Duration",
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => `${row.unitDurationMinutes}m + ${row.bufferMinutes}m`,
    },
    {
      field: "maxQuantity",
      headerName: "Quantity",
      minWidth: 90,
      renderCell: ({ value }) => `1–${value}`,
    },
    {
      field: "payment",
      headerName: "Payment",
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => [row.allowPayNow && "Pay Now", row.allowPayAtService && "At Service"]
        .filter(Boolean).join(" + "),
    },
    {
      field: "status",
      headerName: "Status / version",
      minWidth: 125,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Chip
            size="small"
            color={row.status === "ACTIVE" ? "success" : row.status === "PAUSED" ? "warning" : "default"}
            label={quickBookStatusLabel(row.status)}
          />
          <Typography sx={{ fontSize: ".65rem" }}>v{row.version}</Typography>
        </Box>
      ),
    },
    { field: "mappedExpertCount", headerName: "Mapped experts", minWidth: 120, type: "number" },
    {
      field: "updatedAt",
      headerName: "Last updated",
      minWidth: 170,
      renderCell: ({ row }) => (
        <Box>
          <Typography sx={{ fontSize: ".7rem" }}>
            {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: ".6rem" }}>
            {row.updatedBy ? `Admin #${row.updatedBy}` : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 80,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <PermissionGuard feature="service" permission="canEdit">
          <Tooltip title="Edit Quick Book service">
            <IconButton
              aria-label={`Edit ${row.customerTitle}`}
              color="warning"
              size="small"
              onClick={() => navigate(`/admin/quick-book/services/${row.serviceId}/edit`)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </PermissionGuard>
      ),
    },
  ], [navigate]);

  const applyFilters = (values) => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters({
      search: values.search.trim(),
      mode: values.mode,
      status: values.status,
    });
  };

  const clearFilters = () => {
    const empty = { search: "", mode: "", status: "" };
    reset(empty);
    setPaginationModel((current) => ({ ...current, page: 0 }));
    setFilters(empty);
  };

  return (
    <>
      <PageHeaderLayout headerTitle="Quick Book Offerings">
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1, width: { xs: "100%", sm: "auto" } }}>
          <Button variant="outlined" size="small" startIcon={<AccountTreeIcon />} onClick={() => navigate("/admin/quick-book/catalog")}>Catalog structure</Button>
          <PermissionGuard feature="service" permission="canCreate">
          <Button
            variant="contained"
            size="small"
            endIcon={<AddCircleIcon />}
            onClick={() => navigate("/admin/quick-book/services/new")}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Add offering
          </Button>
          </PermissionGuard>
        </Box>
      </PageHeaderLayout>

      <Card component="form" onSubmit={handleSubmit(applyFilters)} sx={{ mb: 1, p: 1.5 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={5}>
            <Controller
              name="search"
              control={control}
              render={({ field }) => <TextField {...field} fullWidth size="small" label="Search title or summary" />}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Controller
              name="mode"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth size="small" label="Mode">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="FIXED_SERVICE">Fixed service</MenuItem>
                  <MenuItem value="INSPECTION_FIRST">Inspection</MenuItem>
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select fullWidth size="small" label="Status">
                  <MenuItem value="">All</MenuItem>
                  {["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"].map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: "flex", gap: 1, justifyContent: { xs: "stretch", md: "flex-end" } }}>
            <Button type="submit" variant="contained" size="small" startIcon={<SearchIcon />} fullWidth>Search</Button>
            <Button type="button" variant="outlined" size="small" onClick={clearFilters} fullWidth>Clear</Button>
            <Tooltip title="Refresh">
              <IconButton aria-label="Refresh Quick Book services" onClick={fetchServices} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      <Card sx={{ width: "100%", overflowX: "auto" }}>
        <Box sx={{ minWidth: 1180 }}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(row) => row.serviceId}
            getRowHeight={() => "auto"}
            loading={loading}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            disableRowSelectionOnClick
            slots={{ noRowsOverlay: EmptyRows }}
            sx={{
              border: 0,
              "& .MuiDataGrid-cell": { alignItems: "center", minHeight: 64, py: 1 },
              "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600 },
            }}
          />
        </Box>
      </Card>
    </>
  );
}
